/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { ChangeEvent, useCallback } from 'react';
import {
  Text,
  Textarea,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  Box,
  useToast,
  Collapse,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { confirmationModalHeaderCSS } from './ConfirmationModal.styles';
import { SeekersPresenter } from '../../../sidebar/seekerList/seekersPresenter';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { TerminationReasonEnum, TerminationTypeEnum } from './UnhireTypeEnum';
import { SeekerContainerTabItems, useSeekerContainerContext } from '../../SeekerContainerContext';
import { useMessagingViewContext } from '../../../MessagingViewContext';
import { SESSION_KEY_CANDIDATE_LIST_TYPE, useSeekersFilters } from '../../../sidebar/filters/SeekersFiltersContext';
import { CandidateListType } from '../../../sidebar/filters/seekersFiltersContextValueType';
import { MESSAGING_TABS, PATH_MAIN } from '../../../../../routes/constants';
import { CandidateCategory, EngagedCandidate } from '../../../../../firebase/firestore/documents/candidate';
import colors from '../../../../../styles/colors';

export type UnhireConfirmationModalProps = {
  isOpen: boolean;
  disabled: boolean;
  onClose: () => void;
  isMessagingView: boolean;
};

export const UnhireConfirmationModal = ({
  isOpen,
  disabled,
  onClose,
  isMessagingView,
}: UnhireConfirmationModalProps): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const toast = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [noteContent, setNoteContent] = React.useState('');
  const [terminationType, setTerminationType] = React.useState<TerminationTypeEnum | ''>('');
  const [terminationReason, setTerminationReason] = React.useState<TerminationReasonEnum | ''>('');
  const [notEligibleForRehire, setNotEligibleForRehire] = React.useState(false);

  const seekerList: SeekersPresenter | undefined = useStoreState((s) => s.candidate.allCandidates);
  const unHireCandidate = useStoreActions((actions) => actions.messaging.unHireCandidate);
  const { selectedCandidate } = useSeekerContainerContext();
  const { setListType } = useSeekersFilters();
  const { setSelectedEngagedCandidate, setSelectedEngagedSeeker } = useMessagingViewContext();

  React.useEffect(
    () => () => {
      setIsLoading(false);
    },
    [],
  );

  const updateNoteContent = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContent(e.target.value);
  };

  const resetForm = () => {
    setNoteContent('');
    setTerminationType('');
    setTerminationReason('');
    setNotEligibleForRehire(false);
  };

  const clearAndClose = () => {
    resetForm();
    onClose();
  };

  const onSelectNextCandidateAfterUnhire = useCallback(
    () => {
      const newSeeker = seekerList?.engagedCandidates.filter(
        (seeker: EngagedCandidate) =>
          seeker.computedStatus?.category === CandidateCategory.Other && seeker.id !== window.location.pathname.split('/')[2],
      );

      if (newSeeker && newSeeker.length > 0) {
        sessionStorage.setItem(SESSION_KEY_CANDIDATE_LIST_TYPE, CandidateListType.ARCHIVED_CANDIDATE_LIST);
        setListType(CandidateListType.ARCHIVED_CANDIDATE_LIST);
        setSelectedEngagedCandidate(newSeeker[0]);
        setSelectedEngagedSeeker(newSeeker[0] && { seekerId: newSeeker[0].seeker, candidateId: newSeeker[0].id });

        history.push(MESSAGING_TABS(newSeeker[0].id, SeekerContainerTabItems.Messaging));
      } else {
        sessionStorage.setItem(SESSION_KEY_CANDIDATE_LIST_TYPE, CandidateListType.UNKNOWN);
        setListType(CandidateListType.UNKNOWN);
        setSelectedEngagedCandidate(undefined);
        setSelectedEngagedSeeker(undefined);
        history.push(PATH_MAIN);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSelectedEngagedCandidate, setSelectedEngagedSeeker, seekerList],
  );

  const handleUnhireRequest = async () => {
    if (!selectedCandidate) {
      toast({
        title: t('notes:noSelectedCandidateMsg.title'),
        description: t('notes:noSelectedCandidateMsg.description'),
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      await unHireCandidate({
        candidateId: selectedCandidate.id,
        accountId: selectedCandidate.account,
        type: terminationType as TerminationTypeEnum,
        reason: terminationReason as TerminationReasonEnum,
        customReason: terminationReason === TerminationReasonEnum.OTHERS ? noteContent : undefined,
        blacklist: !!notEligibleForRehire,
        offboardedTime: new Date().toISOString(),
      });

      clearAndClose();

      if (isMessagingView) {
        onSelectNextCandidateAfterUnhire();
      }

      toast({
        title: t('notes:noteSuccessMsg.title'),
        description: t('notes:noteSuccessMsg.description'),
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isConfirmDisabled =
    !terminationType ||
    !terminationReason ||
    (terminationReason === TerminationReasonEnum.OTHERS && noteContent.trim().length === 0);

  const labelStyle = {
    color: colors.gray[450],
    fontWeight: '700' as const,
  };

  return (
    <Modal id="UnhireConfirmationModal" isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false} size="lg">
      <ModalOverlay>
        <ModalContent>
          <ModalHeader css={confirmationModalHeaderCSS} className="modal-header" style={{ color: 'black' }}>
            {t('messaging:actions.hire.rejectModal.unhireCandidate')}
          </ModalHeader>
          <ModalBody p={6}>
            <FormControl mb="1rem" isRequired>
              <FormLabel {...labelStyle}>{t('messaging:actions.hire.rejectModal.terminationType')}</FormLabel>
              <Select
                value={terminationType}
                placeholder={t('messaging:actions.hire.rejectModal.terminationTypePlaceholder')}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setTerminationType(e.target.value as TerminationTypeEnum)}
                backgroundColor={colors.input.background}
                border="none"
                _placeholder={{
                  color: colors.input.placeholderColor,
                }}
                data-testid="TerminationTypeSelect"
              >
                <option value={TerminationTypeEnum.VOLUNTARY}>
                  {t('messaging:actions.hire.rejectModal.terminationTypeVoluntary')}
                </option>
                <option value={TerminationTypeEnum.INVOLUNTARY}>
                  {t('messaging:actions.hire.rejectModal.terminationTypeInvoluntary')}
                </option>
              </Select>
            </FormControl>

            <FormControl mb="1rem" isRequired>
              <FormLabel {...labelStyle}>{t('messaging:actions.hire.rejectModal.terminationReason')}</FormLabel>
              <Select
                value={terminationReason}
                placeholder={t('messaging:actions.hire.rejectModal.terminationReasonPlaceholder')}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  setTerminationReason(e.target.value as TerminationReasonEnum);
                  if (e.target.value !== TerminationReasonEnum.OTHERS) {
                    setNoteContent('');
                  }
                }}
                backgroundColor={colors.input.background}
                border="none"
                _placeholder={{
                  color: colors.input.placeholderColor,
                }}
                data-testid="TerminationReasonSelect"
              >
                <option value={TerminationReasonEnum.END_OF_CONTRACT}>
                  {t('messaging:actions.hire.rejectModal.terminationReasonEndOfContract')}
                </option>
                <option value={TerminationReasonEnum.OTHERS}>
                  {t('messaging:actions.hire.rejectModal.terminationReasonOthers')}
                </option>
              </Select>
            </FormControl>

            <Collapse in={terminationReason === TerminationReasonEnum.OTHERS} animateOpacity unmountOnExit>
              <Box mb="1rem">
                <FormControl isRequired>
                  <FormLabel {...labelStyle}>{t('messaging:actions.hire.rejectModal.additionalNote')}</FormLabel>
                  <Textarea
                    value={noteContent}
                    data-testid="NoteContent"
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateNoteContent(e)}
                    focusBorderColor="gray"
                    backgroundColor={colors.input.background}
                    border="none"
                    placeholder={t('messaging:actions.hire.rejectModal.terminationNoteplaceholder')}
                    _placeholder={{
                      color: colors.input.placeholderColor,
                    }}
                  />
                </FormControl>
              </Box>
            </Collapse>

            <Box mt="1rem">
              <Checkbox
                isChecked={notEligibleForRehire}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setNotEligibleForRehire(e.target.checked)}
                data-testid="NotEligibleForRehireCheckbox"
              >
                <Text>{t('messaging:actions.hire.rejectModal.notEligibleForRehire')}</Text>
              </Checkbox>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button data-testid="CancelButton" colorScheme="red" mr={3} onClick={clearAndClose} disabled={isLoading}>
              {t('messaging:actions.hire.confirmationModal.cancelButton')}
            </Button>
            <Button
              data-testid="ConfirmButton"
              colorScheme="blue"
              onClick={handleUnhireRequest}
              isLoading={isLoading}
              disabled={isConfirmDisabled || disabled || isLoading}
            >
              {t('messaging:actions.hire.confirmationModal.saveButton')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};
